/*
 * @FileName       : reports.saga.js
 * @Version        : 1.0
 * @DateOfCreation : 04/21/2020
 * @Author         : $u^^!t @ FFF Enterprises
 * @Dependencies   : N/A
 * @Description    : User saga to store all the reports events.
 *
 * Copyright (c) 2020, FFF Enterprises. All rights reserved.
 */

import { call, put, select, delay } from 'redux-saga/effects';
import { TenantsActions, UIAction, UserActions } from '../actions/index';
import { TenantsService } from '../services';
import { ResultSet } from '../models/common/result.set';
import { TenantsList } from '../models/tenants/tenants';
import { Constants } from '../../constants/Constants';

export function* getTenantsList(action: ReturnType<typeof TenantsActions.getTenantList>) {
	try {
		let callback = action.payload.callback ? action.payload.callback : null;
		delete action.payload.callback;
		const { data } = yield call(TenantsService.getInstance().tenantsList, action.payload);
		if (callback) {
			callback(data);
		}
		yield put(TenantsActions.successTenantsList(data));
	} catch (err) {
		console.log('saga error', err);
	}
}

export function* getTenantsDetails(action: ReturnType<typeof TenantsActions.getTenantsDetails>) {
	try {
		yield put(UIAction.showLoader(true));
		yield put(UIAction.setApiError(null));
		const { data } = yield call(TenantsService.getInstance().tenantsList, action.payload);
		yield put(TenantsActions.successTenantsDetails(data));
		yield put(UIAction.showLoader(false));
		if (!data || !data.result || data.result.length === 0) {
			yield put(
				UserActions.setSnackbarMessage({
					message: Constants.ALERT.NO_RECORD_FOUND,
					type: 'info',
				}),
			);
		}
	} catch (err: any) {
		console.log('saga error' + err);
		yield put(UIAction.showLoader(false));
		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;

		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in	getTenantsDetails`,
				type: 'error',
			}),
		);
	}
}

export function* resetTenantsList(action: ReturnType<typeof TenantsActions.clearTenantsList>): any {
	if (action.payload) {
		const userTenants = yield select(state => state.user.authUser.record.tenants);
		const data: ResultSet<TenantsList> = {
			result: userTenants,
			total_record: userTenants.length as number,
		};
		yield put(TenantsActions.successTenantsList(data));
	}
}
export function* getTenantBasicInfo(action: ReturnType<typeof TenantsActions.getTenantBasicInfo>) {
	try {
		yield put(UIAction.showLoader(true));
		const { data } = yield call(TenantsService.getInstance().tenantsList, action.payload);
		yield put(TenantsActions.successTenantBasicInfo(data));
		yield put(UIAction.showLoader(false));
	} catch (err) {
		console.log('saga error' + err);
		yield put(UIAction.showLoader(false));
	}
}

export function* getTenantAdditionalInfo(action: ReturnType<typeof TenantsActions.getTenantAdditionalInfo>) {
	try {
		yield put(UIAction.showLoader(true));
		const { data } = yield call(TenantsService.getInstance().tenantsAdditionalInfo, action.payload);
		yield put(TenantsActions.setTenantAdditionalInfo(data));
		yield put(UIAction.showLoader(false));
	} catch (err) {
		console.log('saga error' + err);
		yield put(UIAction.showLoader(false));
	}
}

/*export function* getInventoryGroup(action: ReturnType<typeof ReportActions.getInventoryGroup>) {
    try {
        const { response }  = yield call(ReportService.getInstance().inventoryGroupByTenants, action.payload);
        yield put(ReportActions.successInventoryGroup(response.data));
    } catch (err) {
        console.log("saga error" + err);
    }
}*/

export function* getTenant(action: ReturnType<typeof TenantsActions.getTenant>) {
	try {
		yield put(UIAction.showLoader(true));
		const { data } = yield call(TenantsService.getInstance().searchTenant, action.payload);
		yield put(TenantsActions.successTenantsList(data));
		yield put(UIAction.showLoader(false));
	} catch (err) {
		yield put(UIAction.showLoader(false));
		console.log('saga error', err);
	}
}

export function* getNotificationActivity(action: ReturnType<typeof TenantsActions.getNotificationActivity>): any {
	try {
		yield put(UIAction.showLoader(true));
		yield put(TenantsActions.setNotificationActivity(null));
		const messages = yield call(TenantsService.getInstance().getNotificationActivity, action.payload);
		yield put(TenantsActions.setNotificationActivity(messages));
		yield put(UIAction.showLoader(false));
	} catch (err: any) {
		console.log('notification activity saga error', err);
		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;
		yield put(UIAction.showLoader(false));
		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in getNotificationActivity`,
				type: 'error',
			}),
		);
	}
}

export function* getNotificationMessage(action: ReturnType<typeof TenantsActions.getNotificationActivity>): any {
	try {
		yield put(UIAction.showLoader(true));
		const message = yield call(TenantsService.getInstance().getNotificationMessage, action.payload);
		yield put(TenantsActions.setNotificationMessage(message));
		yield put(UIAction.showLoader(false));
	} catch (err: any) {
		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;
		yield put(UIAction.showLoader(false));
		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in getNotificationMessage`,
				type: 'error',
			}),
		);
	}
}

export function* syncCustomer(action: ReturnType<typeof TenantsActions.syncCustomer>) {
	try {
		yield put(UIAction.showLoader(true));
		const callback = action.payload.callback;
		delete action.payload.callback;
		yield call(TenantsService.getInstance().syncCustomer, action.payload);
		if (callback) {
			yield call(callback, false);
		}
		yield put(UIAction.showLoader(false));
	} catch (err: any) {
		yield put(UIAction.showLoader(false));
		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;
		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in syncCustomer`,
				type: 'error',
			}),
		);
	}
}

export function* updateCustomer(action: ReturnType<typeof TenantsActions.updateCustomer>) {
	try {
		yield call(TenantsService.getInstance().updateCustomer, action.payload);
		action.payload.success();
	} catch (err: any) {
		yield put(UIAction.showLoader(false));
		const errorMessage = err.message.includes('403') ? Constants.ALERT.NOT_AUTHORIZED : Constants.ALERT.SERVER_ERROR;
		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${errorMessage} in updateCustomer`,
				type: 'error',
			}),
		);
	}
}

export function* getCustomerProducts(action: ReturnType<typeof TenantsActions.getTenantProducts>) {
	try {
		yield put(UIAction.showLoader(true));
		const { data } = yield call(TenantsService.getInstance().getTenantProducts, action.payload);
		yield put(TenantsActions.setTenantProducts(data));
		yield put(UIAction.showLoader(false));
	} catch (err) {
		console.log('Tenant Products Saga Error', err);
		yield put(UIAction.showLoader(false));

		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${err}`,
				type: 'error',
			}),
		);
	}
}

export function* createCustomerProduct(action: ReturnType<typeof TenantsActions.createCustomerProduct>) {
	try {
		yield put(UIAction.showLoader(true));
		yield call(TenantsService.getInstance().createCustomerProduct, action.payload);
		yield put(UIAction.showLoader(false));
	} catch (err) {
		console.log('Create Customer Product Saga Error', err);
		yield put(UIAction.showLoader(false));

		yield put(
			UserActions.setSnackbarMessage({
				message: `An error occurred: ${err}`,
				type: 'error',
			}),
		);
	}
}
